import { useState, useEffect } from 'react';
import { useQuery } from '@redux-requests/react';
import {
  addUser,
  inviteMember,
  updateUser,
  updateUserAdditionalPermissions,
  fetchActiveUserOrganization,
} from 'store/myAccount/requests';
import { getRoles, getUserById } from 'store/myAccount/requests';
import { useDispatch } from 'react-redux';
import { Divider, Button, RadioGroup, FormControlLabel, DialogActions } from '@mui/material';
import { User } from '@auth0/auth0-react';
import { getAssignableRoles, isSuperAdmin } from 'utils/auth';
import AuditTrailPermissionField from './AuditTrailPermissionField';
import RoleHeader from './RoleHeader';
import { isUserPrimaryRoleSelected } from './auditTrailPermission';
import { Formik, Form, FieldProps, Field } from 'formik';
import { UserRolesData, TeamMemberData, UserRolesName } from 'types';
import { GVLoadingBackdrop } from 'components/common';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import GVRadio from 'components/lib/GVRadio/GVRadio';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import VerifyDialog from '../../../components/common/VerifyDialog/VerifyDialog';
import { GVTypography } from '../../../components/lib';
import { useUserStateStore } from 'zstore/userStateStore';
import styles from './TeamMemberModal.module.css';

type TeamMemberModalProps = {
  title: string;
  subtitle: string;
  iconType: string;
  open: boolean;
  member?: TeamMemberData;
  userId?: string;
  edit?: boolean;
  onClose: () => void;
  ssoEnabled?: boolean;
  isWhitelisted?: boolean;
  permissionOnly?: boolean;
};

const TeamMemberModal = ({
  title,
  subtitle,
  open,
  userId,
  edit,
  onClose,
  iconType,
  ssoEnabled,
  isWhitelisted,
  permissionOnly,
}: TeamMemberModalProps) => {
  const dispatch = useDispatch();
  const fetchRoles = useQuery(getRoles());
  const roles: Array<UserRolesData> = fetchRoles.data || [];
  const roleUserId = roles.find((role) => role.name === 'user')?.id;
  const { data: user, loading } = useQuery(getUserById(userId, isWhitelisted));
  const { data: orgData } = useQuery({ type: fetchActiveUserOrganization });
  const [modalOpen, setModalOpen] = useState(open);
  const [initialValues, setInitialValues] = useState<TeamMemberData>({
    // users belonging to an sso enabled tenant will not have firstName/lastName
    ...(!ssoEnabled && { firstName: '', lastName: '' }),
    role: roleUserId,
    email: '',
    hasAuditTrail: false,
  });
  // const { user: auth0User } = useAuth0();
  const { user: auth0User } = useUserStateStore();
  const superAdmin = isSuperAdmin(auth0User as User);

  useEffect(() => {
    if (edit) {
      const newInitialValues = {
        ...(!ssoEnabled && { firstName: user?.firstName || '', lastName: user?.lastName || '' }),
        role: user?.role || roleUserId,
        email: user?.email || '',
        hasAuditTrail: user?.hasAuditTrail ?? false,
      };

      setInitialValues(newInitialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, edit]);

  const validateForm = (values: TeamMemberData) => {
    const errors: Partial<TeamMemberData> = {};
    if (permissionOnly) {
      return errors;
    }
    if ('firstName' in values && !values.firstName?.trim()) {
      errors.firstName = 'Required field';
    }
    if ('lastName' in values && !values.lastName?.trim()) {
      errors.lastName = 'Required field';
    }
    if (!values.email.trim()) {
      errors.email = 'Required field';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i.test(values.email)) {
      errors.email = 'Invalid email address';
    }
    return errors;
  };

  const userIsFromConnection = (email: string): boolean => {
    if (
      !orgData?.connection?.configurationSettings?.domain ||
      orgData?.connection?.configurationSettings?.domain.length <= 0
    ) {
      return false;
    }
    const userDomain = email.split('@')[1];
    return orgData.connection?.configurationSettings?.domain.some((domain) => domain === userDomain);
  };

  const handleSubmit = (values: TeamMemberData) => {
    if (!edit) {
      if (
        orgData?.connection &&
        orgData?.connection.connectionName !== 'Username-Password-Authentication' &&
        userIsFromConnection(values.email)
      ) {
        dispatch(inviteMember(values, orgData?.id));
      } else if (orgData?.id) {
        dispatch(addUser(orgData.id, values, roles));
      }
    } else if (orgData?.id && userId) {
      dispatch(
        permissionOnly
          ? updateUserAdditionalPermissions(orgData.id, userId, values.hasAuditTrail ?? false)
          : updateUser(orgData.id, values, userId, initialValues, roles, isWhitelisted),
      );
    }

    setModalOpen(false);
    onClose();
  };

  const renderAddTeamMemberForm = () => (
    <>
      {loading ? (
        <GVLoadingBackdrop />
      ) : (
        <Formik
          key="settingsForm"
          initialValues={initialValues}
          enableReinitialize
          validate={validateForm}
          onSubmit={handleSubmit}
        >
          {(formikProps) => (
            <Form id="add-team-member-form" onSubmit={formikProps.handleSubmit} className={styles.form}>
              <div className={styles.section}>
                <GVTypography variant="subtitle1">Team Member Info</GVTypography>
              </div>
              <div className={styles.fields}>
                {!ssoEnabled && (
                  <>
                    <Field name="firstName">
                      {(fieldProps: FieldProps) => (
                        <GVTextField
                          name={fieldProps.field.name}
                          hasError={!!fieldProps.meta.error}
                          message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                          value={fieldProps.field.value}
                          onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                          label="First Name"
                          id="first-name-member"
                          data-testid="first_name_member"
                          isDisabled={permissionOnly}
                        />
                      )}
                    </Field>
                    <Field name="lastName">
                      {(fieldProps: FieldProps) => (
                        <GVTextField
                          name={fieldProps.field.name}
                          hasError={!!fieldProps.meta.error}
                          message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                          value={fieldProps.field.value}
                          onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                          id="last-name-member"
                          data-testid="last_name_member"
                          label="Last Name"
                          isDisabled={permissionOnly}
                        />
                      )}
                    </Field>
                  </>
                )}
                <GVTooltip
                  title="These settings are managed by your identity provider."
                  disableHoverListener={!permissionOnly}
                >
                  <div className={styles.full}>
                    <Field name="email">
                      {(fieldProps: FieldProps) => (
                        <GVTextField
                          name={fieldProps.field.name}
                          hasError={!!fieldProps.meta.error}
                          message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                          value={fieldProps.field.value}
                          onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                          id="email-member"
                          data-testid="email_member"
                          label="Email"
                          isDisabled={edit || permissionOnly}
                        />
                      )}
                    </Field>
                  </div>
                </GVTooltip>
              </div>

              <Divider light className={styles.rule} />

              <RoleHeader showInfo={!permissionOnly} />
              <Field name="role">
                {(fieldProps: FieldProps) => (
                  <GVTooltip
                    title="These settings are managed by your identity provider."
                    disableHoverListener={!permissionOnly}
                  >
                    <RadioGroup
                      aria-label="role"
                      name={fieldProps.field.name}
                      value={fieldProps.field.value}
                      onChange={(event) => {
                        fieldProps.field.onChange(event);
                        if (!isUserPrimaryRoleSelected(event.target.value, roles)) {
                          formikProps.setFieldValue('hasAuditTrail', false);
                        }
                      }}
                      row
                    >
                      {getAssignableRoles(roles, superAdmin).map((role) => {
                        return (
                          <FormControlLabel
                            className={styles.roles}
                            key={role.id}
                            value={role.id}
                            control={<GVRadio color="secondary" className={styles.radio} />}
                            label={UserRolesName[role.name]}
                            disabled={permissionOnly}
                          />
                        );
                      })}
                    </RadioGroup>
                  </GVTooltip>
                )}
              </Field>
              <AuditTrailPermissionField roleId={formikProps.values.role} roles={roles} />
              <Divider light className={styles.rule} />
              <DialogActions>
                <Button color="inherit" id="add-team-member-form-cancel-button" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  id="dialog-ok"
                  disabled={!formikProps.isValid || !formikProps.dirty}
                  type="submit"
                  color="secondary"
                  form="add-team-member-form"
                  variant="contained"
                >
                  {edit ? 'Save' : 'Send Invite'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      )}
    </>
  );

  if (edit && loading) {
    return <GVLoadingBackdrop />;
  }

  const modalProps = {
    title,
    subTitle: subtitle,
    open: modalOpen,
    callToActionText: edit ? 'Save' : 'Send Invite',
    callToActionButtonType: 'submit' as 'button' | 'reset' | 'submit' | undefined,
    iconType,
    isDivider: true,
    children: renderAddTeamMemberForm(),
    hideCallToAction: true,
    handleCallToActionClick: () => {},
    handleCloseModal: onClose,
  };

  return <VerifyDialog {...modalProps} />;
};

export default TeamMemberModal;
